import { useEffect, useState } from "react";
import { ChevronsLeftRight } from "lucide-react";
import { Kicker, Reveal } from "../components/Reveal";
import { api } from "../lib/api";

const resolve = (u) => (u.startsWith("http") ? u : `${process.env.REACT_APP_BACKEND_URL}${u}`);

function BeforeAfterCard({ item, index }) {
  const [pos, setPos] = useState(50);
  return (
    <Reveal delay={index * 0.08}>
      <article data-testid={`gallery-card-${item.id}`}>
        <div className="relative aspect-square select-none overflow-hidden rounded-lg border border-linen">
          <img src={resolve(item.after_url)} alt={`${item.treatment} — after`} className="absolute inset-0 h-full w-full object-cover" draggable={false} loading="lazy" />
          <div className="absolute inset-0" style={{ clipPath: `inset(0 ${100 - pos}% 0 0)` }}>
            <img src={resolve(item.before_url)} alt={`${item.treatment} — before`} className="absolute inset-0 h-full w-full object-cover" draggable={false} loading="lazy" />
          </div>
          <div className="pointer-events-none absolute inset-y-0 w-px -translate-x-1/2 bg-ivory" style={{ left: `${pos}%` }}>
            <span className="absolute left-1/2 top-1/2 flex h-9 w-9 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-ivory text-forest shadow-sm">
              <ChevronsLeftRight size={16} />
            </span>
          </div>
          <span className="pointer-events-none absolute left-3 top-3 rounded-md bg-ivory/90 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.15em] text-forest">Before</span>
          <span className="pointer-events-none absolute right-3 top-3 rounded-md bg-ivory/90 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.15em] text-forest">After</span>
          <input
            type="range"
            min="0"
            max="100"
            value={pos}
            onChange={(e) => setPos(Number(e.target.value))}
            aria-label={`Compare before and after — ${item.treatment}`}
            data-testid={`gallery-slider-${item.id}`}
            className="absolute inset-0 h-full w-full cursor-ew-resize appearance-none bg-transparent opacity-0"
          />
        </div>
        <h3 className="mt-5 font-serif text-2xl font-medium text-forest">{item.treatment}</h3>
        <p className="mt-1 text-sm leading-relaxed text-moss">{item.caption}</p>
      </article>
    </Reveal>
  );
}

export default function GallerySection() {
  const [items, setItems] = useState(null);

  useEffect(() => {
    let retried = false;
    const fetchItems = () => {
      api.get("/gallery").then((res) => setItems(res.data)).catch(() => {
        if (!retried) {
          retried = true;
          setTimeout(fetchItems, 3000);
        } else {
          setItems([]);
        }
      });
    };
    fetchItems();
  }, []);

  if (!items || items.length === 0) return null;
  const hasPlaceholders = items.some((i) => i.is_placeholder);

  return (
    <section id="results" data-testid="gallery-section" className="border-t border-linen bg-ivory py-20 sm:py-28">
      <div className="mx-auto max-w-7xl px-6 sm:px-8">
        <Reveal>
          <Kicker>Real Results</Kicker>
          <h2 className="mt-4 max-w-2xl font-serif text-4xl font-medium leading-tight tracking-tight text-forest sm:text-5xl">
            Before &amp; after
          </h2>
          <p className="mt-4 max-w-xl text-sm leading-relaxed text-moss sm:text-base">
            Drag the slider on each photo to compare. Every treatment is planned
            around your features — subtle, natural, never overdone.
          </p>
        </Reveal>

        <div className="mt-12 grid gap-10 sm:grid-cols-2 lg:grid-cols-3">
          {items.map((item, i) => (
            <BeforeAfterCard key={item.id} item={item} index={i} />
          ))}
        </div>

        {hasPlaceholders && (
          <p data-testid="gallery-disclaimer" className="mt-10 text-xs italic text-moss/70">
            Illustrative placeholder imagery shown — real client results coming soon.
          </p>
        )}
      </div>
    </section>
  );
}
